/**
 * Standard modifiers (RB §1.5, §1.2, §3.3, §3.5).
 *
 * **Values are data; predicates are code.** A fully declarative predicate
 * language would need a mini-interpreter nobody wants to debug, so the split is:
 * `value` participates in the content bundle and therefore in the content hash,
 * while `applies()` is ordinary TypeScript.
 *
 * The consequence is important enough to state twice: **a change to a predicate
 * is invisible to the content hash, so it requires a manual `CONTENT_VERSION`
 * bump.** That is the one way the §10.1 mid-exercise rule-drift guard can be
 * quietly defeated. `test/data-tables.test.ts` says the same thing in its failure
 * message.
 *
 * `label` is not decoration. It is what a player reads in the after-action review
 * — "[-4] acting asset DEGRADED" — and it is the mechanism by which players learn
 * the model (§7.4).
 */
export const MODIFIER_SCOPES = ['ALL', 'SENSING', 'ENGAGEMENT', 'CONJUNCTION'];
export const MODIFIERS = Object.freeze([
    {
        id: 'ACTOR_DEGRADED',
        label: 'acting asset DEGRADED',
        value: -4,
        scope: 'ALL',
        applies: (c) => c.actorStatus === 'DEGRADED',
    },
    {
        id: 'TARGET_CHARACTERIZED',
        label: 'target CHARACTERIZED',
        value: 2,
        scope: 'ALL',
        applies: (c) => c.targetCharacterized,
    },
    {
        id: 'TARGET_EMITTING',
        label: 'target is emitting',
        value: 4,
        scope: 'SENSING',
        applies: (c) => c.targetEmitting,
    },
    {
        id: 'TARGET_SIGNATURE_LARGE',
        label: 'target signature LARGE',
        value: 2,
        scope: 'SENSING',
        applies: (c) => c.targetSignature === 'LARGE',
    },
    {
        id: 'TARGET_SIGNATURE_LOW',
        label: 'target signature LOW',
        value: -2,
        scope: 'SENSING',
        applies: (c) => c.targetSignature === 'LOW',
    },
    {
        id: 'TARGET_EMCON',
        label: 'target under Emission Control',
        value: -2,
        scope: 'SENSING',
        applies: (c) => c.targetEmcon,
    },
    {
        // RB §1.2 cites this as a §1.5 modifier; §1.5 does not list it. Encoded here
        // because §1.2's "Maneuver and sensing" table is unambiguous about the value.
        id: 'BURN_SIGNATURE',
        label: 'target manoeuvred this turn (burn signature)',
        value: 4,
        scope: 'SENSING',
        applies: (c) => c.targetManeuveredThisTurn,
    },
    {
        // RB §3.3 — in addition to the burn signature on the departure turn.
        id: 'TARGET_IN_TRANSIT',
        label: 'target IN TRANSIT',
        value: 4,
        scope: 'SENSING',
        applies: (c) => c.targetInTransit && c.isWideSearch,
    },
    {
        id: 'TARGET_STATIC',
        label: 'target is STATIC',
        value: 4,
        scope: 'SENSING',
        applies: (c) => c.targetStatic,
    },
]);
export const MODIFIERS_BY_ID = Object.freeze(Object.fromEntries(MODIFIERS.map((m) => [m.id, m])));
/** Everything in scope for a roll, itemised. Order is table order, so it is stable. */
export function assembleModifiers(scope, ctx) {
    return MODIFIERS.filter((m) => (m.scope === 'ALL' || m.scope === scope) && m.applies(ctx)).map((m) => ({ source: m.id, label: m.label, value: m.value }));
}
//# sourceMappingURL=modifiers.js.map